import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { XMarkIcon } from '@heroicons/react/24/outline';

/** 推荐海报标语项 */
export interface PosterSlogan {
  id: string;
  label: string;
  text: string;
}

/** 内置默认标语（后端未配置时回退使用） */
export const POSTER_SLOGANS: PosterSlogan[] = [
  { id: 'official', label: '对外正式版', text: '从容投标，稳稳赢标——WinBid。' },
  {
    id: 'shortvideo',
    label: '短视频/海报版',
    text: '投标不必匆匆忙忙、连滚带爬；用 WinBid，从从容容、游刃有余。',
  },
  { id: 'brand', label: '品牌双关版', text: 'WinBid，让“中标”从运气变成流程。' },
];

interface ReferralPosterProps {
  open: boolean;
  /** 推荐链接（将编码进二维码） */
  link: string;
  onClose: () => void;
  /** 后端下发的标语列表；缺省时使用内置默认 */
  slogans?: PosterSlogan[];
}

const POSTER_W = 600;
const POSTER_H = 840;

export default function ReferralPoster({ open, link, onClose, slogans: slogansProp }: ReferralPosterProps) {
  const slogans = slogansProp && slogansProp.length > 0 ? slogansProp : POSTER_SLOGANS;
  const [sloganId, setSloganId] = useState(slogans[0].id);
  const slogan = slogans.find((s) => s.id === sloganId)?.text ?? slogans[0].text;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrReady, setQrReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 标语列表变化时重置选中项
  useEffect(() => {
    if (slogans.length) setSloganId(slogans[0].id);
  }, [slogans]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setQrReady(false);
    setError(null);
    (async () => {
      try {
        const qr = await QRCode.toDataURL(link, {
          width: 280,
          margin: 2,
          errorCorrectionLevel: 'M',
          color: { dark: '#4c1d95', light: '#ffffff' },
        });
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d');
          if (!canvas || !ctx) return;
          drawPoster(ctx, img, { link, slogan });
          setQrReady(true);
        };
        img.onerror = () => {
          if (!cancelled) setError('二维码渲染失败');
        };
        img.src = qr;
      } catch {
        if (!cancelled) setError('生成二维码失败');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, link, slogan]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = 'winbid-referral-poster.png';
    a.click();
  };

  const handleCopy = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (blob && navigator.clipboard && 'write' in navigator.clipboard) {
        const ClipboardItemCtor = (window as any).ClipboardItem;
        if (ClipboardItemCtor) {
          await (navigator.clipboard as any).write([new ClipboardItemCtor({ 'image/png': blob })]);
        }
      }
    } catch {
      // 复制失败不影响其它功能
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">推荐海报</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 flex flex-col items-center gap-4">
          {/* 标语选择 */}
          <div className="w-full">
            <div className="text-xs text-gray-500 mb-2">选择宣传标语</div>
            <div className="flex flex-wrap gap-2">
              {slogans.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSloganId(s.id)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    sloganId === s.id
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="text-xs text-red-500">{error}</div>}

          <canvas
            ref={canvasRef}
            width={POSTER_W}
            height={POSTER_H}
            className="rounded-lg border border-gray-200 shadow-sm"
            style={{ width: '100%', maxWidth: '300px', height: 'auto' }}
          />

          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={!qrReady}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              下载海报
            </button>
            <button
              onClick={handleCopy}
              disabled={!qrReady}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              复制图片
            </button>
          </div>
          <p className="text-[11px] text-gray-400 text-center">
            海报含专属推荐二维码，好友扫码注册后双方均可获得积分奖励
          </p>
        </div>
      </div>
    </div>
  );
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const ch of text) {
    const test = line + ch;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = ch;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawPoster(
  ctx: CanvasRenderingContext2D,
  qr: HTMLImageElement,
  opts: { link: string; slogan: string }
) {
  const W = POSTER_W;
  const H = POSTER_H;

  // 背景渐变（紫 → 粉）
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, '#6d28d9');
  g.addColorStop(0.55, '#7c3aed');
  g.addColorStop(1, '#db2777');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // 顶部柔光装饰
  ctx.save();
  ctx.globalAlpha = 0.12;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(W / 2, -120, 260, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // 品牌区
  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffffff';
  ctx.font = '700 42px "PingFang SC", "Microsoft YaHei", sans-serif';
  ctx.fillText('WinBid', W / 2, 96);
  ctx.font = '500 18px "PingFang SC", "Microsoft YaHei", sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillText('推 荐 有 礼', W / 2, 126);

  // 分割线
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 40, 144);
  ctx.lineTo(W / 2 + 40, 144);
  ctx.stroke();

  // 宣传标语（自动换行）
  ctx.fillStyle = '#ffffff';
  ctx.font = '600 26px "PingFang SC", "Microsoft YaHei", sans-serif';
  const lines = wrapText(ctx, opts.slogan, W - 100).slice(0, 4);
  let y = 200;
  const lineH = 40;
  lines.forEach((line) => {
    ctx.fillText(line, W / 2, y);
    y += lineH;
  });

  // 白色卡片承载二维码
  const cardW = 340;
  const cardH = 384;
  const cardX = (W - cardW) / 2;
  const cardY = 372;
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, cardX, cardY, cardW, cardH, 20);
  ctx.fill();

  // 二维码（正方形，居中）
  const qrSize = 280;
  const qrX = (W - qrSize) / 2;
  const qrY = cardY + 24;
  ctx.drawImage(qr, qrX, qrY, qrSize, qrSize);

  // 二维码下方说明
  ctx.fillStyle = '#6d28d9';
  ctx.font = '600 18px "PingFang SC", "Microsoft YaHei", sans-serif';
  ctx.fillText('扫码注册 WinBid', W / 2, qrY + qrSize + 34);
  ctx.fillStyle = '#9ca3af';
  ctx.font = '400 13px "PingFang SC", "Microsoft YaHei", sans-serif';
  ctx.fillText('邀请好友，双方各得积分', W / 2, qrY + qrSize + 56);

  // 底部推荐链接与口号
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.font = '400 13px "PingFang SC", monospace';
  const linkText = opts.link.length > 42 ? `${opts.link.slice(0, 42)}…` : opts.link;
  ctx.fillText(linkText, W / 2, H - 36);
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.font = '400 12px "PingFang SC", sans-serif';
  ctx.fillText('WinBid · 让每一次投标都更有把握', W / 2, H - 18);
}
